import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router';

import { useApi } from '@/api/useApi.hook';

import { useAuthStore } from './auth.store';
import { hasAdminAccess } from './useAdminAccess';

export function AuthGuard() {
    const api = useApi();
    const { identity, isAccessDenied, setIdentity, setAccessDenied, clear } = useAuthStore();

    const hasSession = api.client.hasSession();

    const myIdentity = useQuery({
        queryKey: ['my-identity'],
        queryFn: () => api.v1.identities.getMyIdentity(),
        refetchInterval: 1000 * 60 * 2,
        enabled: hasSession && !identity,
    });

    useEffect(() => {
        if (myIdentity.data) {
            if (hasAdminAccess(myIdentity.data)) {
                setIdentity(myIdentity.data);
            } else {
                api.client.deleteSession();
                setAccessDenied();
            }
        }
    }, [myIdentity.data]);

    useEffect(() => {
        const onSessionExpired = () => clear();
        const onSessionDeleted = () => clear();

        api.client.addEventListener('sessionExpired', onSessionExpired);
        api.client.addEventListener('sessionDeleted', onSessionDeleted);

        return () => {
            api.client.removeEventListener('sessionExpired', onSessionExpired);
            api.client.removeEventListener('sessionDeleted', onSessionDeleted);
        };
    }, [api.client]);

    if (!hasSession && !isAccessDenied) {
        return <Navigate to="/login" replace />;
    }

    if (isAccessDenied) {
        return <Navigate to="/access-denied" replace />;
    }

    if (!identity) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return <Outlet />;
}
